import { computed, onMounted, ref, watch } from 'vue';
import { usePB } from '@/composables/database/use-pocket-base.ts';
import { publicConfig } from '@/state/config.ts';

type PushSubscriptionRecord = {
  id: string;
};

type PushSubscriptionsService = {
  create: (bodyParams?: Record<string, unknown>) => Promise<PushSubscriptionRecord>;
  delete: (id: string) => Promise<boolean>;
  getFirstListItem: <T>(filter: string) => Promise<T>;
  update: (id: string, bodyParams?: Record<string, unknown>) => Promise<PushSubscriptionRecord>;
};

const pb = usePB();
const isBusy = ref(false);
const permission = ref<NotificationPermission | 'unsupported'>('unsupported');
const isSubscribed = ref(false);
let registrationPromise: Promise<ServiceWorkerRegistration> | undefined;
let initialized = false;

function supportsWebPush() {
  return window.isSecureContext && 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
}

function pushSubscriptions() {
  return pb.db.collection('push_subscriptions') as unknown as PushSubscriptionsService;
}

function getVapidPublicKey() {
  const publicKey = publicConfig.vapidPublicKey;
  if (!publicKey) {
    throw new Error('Web push is not configured for this site.');
  }
  return publicKey;
}

function toApplicationServerKey(value: string) {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
  const decoded = window.atob(base64);
  return Uint8Array.from(decoded, (character) => character.charCodeAt(0));
}

async function getRegistration() {
  if (!supportsWebPush()) {
    throw new Error('Web push is not supported by this browser.');
  }

  registrationPromise ??= navigator.serviceWorker.register('/service-worker.js', { scope: '/' });
  try {
    return await registrationPromise;
  } catch (error) {
    registrationPromise = undefined;
    throw error;
  }
}

async function getSubscription() {
  const registration = await getRegistration();
  return registration.pushManager.getSubscription();
}

function isNotFoundError(error: unknown) {
  return typeof error === 'object' && error !== null && 'status' in error && error.status === 404;
}

async function saveSubscription(userId: string, subscription: PushSubscription) {
  const service = pushSubscriptions();
  const serialized = subscription.toJSON();
  const data = {
    user_id: userId,
    endpoint: subscription.endpoint,
    keys: serialized.keys,
  };
  const filter = pb.db.filter('user_id = {:user} && endpoint = {:endpoint}', { user: userId, endpoint: subscription.endpoint });

  try {
    const existing = await service.getFirstListItem<PushSubscriptionRecord>(filter);
    await service.update(existing.id, data);
  } catch (error) {
    if (!isNotFoundError(error)) throw error;
    await service.create(data);
  }
}

async function removeStoredSubscription(userId: string, endpoint: string) {
  const service = pushSubscriptions();
  const filter = pb.db.filter('user_id = {:user} && endpoint = {:endpoint}', { user: userId, endpoint });

  try {
    const stored = await service.getFirstListItem<PushSubscriptionRecord>(filter);
    await service.delete(stored.id);
  } catch (error) {
    if (!isNotFoundError(error)) throw error;
  }
}

async function refreshStatus() {
  if (!supportsWebPush()) {
    permission.value = 'unsupported';
    isSubscribed.value = false;
    return;
  }

  permission.value = Notification.permission;
  isSubscribed.value = !!(await getSubscription());
}

async function enable() {
  if (isBusy.value) return;
  if (!pb.currentUser.value?.id) throw new Error('Sign in before enabling web push.');

  isBusy.value = true;
  const userId = pb.currentUser.value.id;
  try {
    if (!supportsWebPush()) throw new Error('Web push is not supported by this browser.');

    permission.value = Notification.permission;
    if (permission.value === 'default') {
      permission.value = await Notification.requestPermission();
    }
    if (permission.value !== 'granted') {
      throw new Error('Browser notifications were not allowed.');
    }

    const registration = await getRegistration();
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: toApplicationServerKey(getVapidPublicKey()),
      });
    }

    if (pb.currentUser.value?.id !== userId) {
      await subscription.unsubscribe();
      throw new Error('Your account changed before web push could be enabled.');
    }

    await saveSubscription(userId, subscription);
    isSubscribed.value = true;
  } finally {
    isBusy.value = false;
  }
}

async function disable() {
  if (isBusy.value) return;
  if (!supportsWebPush()) {
    permission.value = 'unsupported';
    isSubscribed.value = false;
    return;
  }

  isBusy.value = true;
  try {
    const userId = pb.currentUser.value?.id;
    const subscription = await getSubscription();

    let storageError: unknown;
    if (userId && subscription) {
      try {
        await removeStoredSubscription(userId, subscription.endpoint);
      } catch (error) {
        storageError = error;
      }
    }
    if (subscription) {
      await subscription.unsubscribe();
    }
    isSubscribed.value = false;
    if (storageError) throw storageError;
  } finally {
    isBusy.value = false;
  }
}

async function discardBrowserSubscription() {
  try {
    const subscription = await getSubscription();
    await subscription?.unsubscribe();
  } finally {
    isSubscribed.value = false;
  }
}

export function initializeWebPush() {
  if (initialized) return;
  initialized = true;

  onMounted(() => {
    let previousUserId = pb.currentUser.value?.id;
    void refreshStatus().catch(() => undefined);

    watch(pb.currentUser, (user) => {
      const userId = user?.id;
      if (previousUserId && previousUserId !== userId) {
        void discardBrowserSubscription().catch(() => undefined);
      }
      previousUserId = userId;

      if (userId) {
        void refreshStatus().catch(() => undefined);
      }
    });
  });
}

export function useWebPush() {
  return {
    disable,
    enable,
    isBusy,
    isSubscribed,
    isSupported: computed(supportsWebPush),
    permission,
    refreshStatus,
  };
}
